import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {LoadingButton} from '@mui/lab';
import {Box, Button, LinearProgress, TextField} from '@mui/material';
import {useFormik} from 'formik';
import _, {kebabCase} from 'lodash';
import {useSnackbar} from 'notistack';
import {useEffect, useMemo} from 'react';
import * as yup from 'yup';

import {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {useAppDispatch} from '../../hooks/redux';
import {useChunkedUpload} from '../../hooks/useChunkedUpload';
import {MapUploadQuery} from '../../interfaces/Map';
import reduxActions from '../../redux/actions';
import {CloseSnackbarAction} from '../common/CloseSnackbarButton';
import SnackbarMessages from '../common/SnackbarMessages';

type MapUploadFormikValue = Omit<
  MapUploadQuery,
  'fileName' | 'chunkIndex' | 'totalChunkCount'
> & {
  file: File | null;
};

interface MapItemUploadProps {
  onCancel?: Function;
  onSubmitted?: () => void;
}

export const MapItemUpload: React.FC<MapItemUploadProps> = ({
  onCancel,
  onSubmitted,
}) => {
  /*********/
  /* input */
  /*********/
  const shouldGeneratePath = true;
  const inputValidationSchema = useMemo(() => {
    const fields: any = {
      file: yup.mixed().nullable().required('Field is reqired'),
      name: yup.string().nullable().required('Field is required'),
      path: yup.string().nullable().required('Field is required'),
    };

    return yup.object().shape(fields);
  }, []);

  const getFormikValues = (): MapUploadFormikValue => ({
    file: null,
    name: null,
    path: null,
  });

  const formik = useFormik<MapUploadFormikValue>({
    initialValues: getFormikValues(),
    validationSchema: inputValidationSchema,
    onSubmit: async () => {
      if (formik.values.file) {
        upload.start(formik.values.file);
      }
    },
  });

  useEffect(() => {
    const newFormikValues = getFormikValues();
    if (!_.isEqual(newFormikValues, formik.values)) {
      formik.setValues(newFormikValues);
    }
  }, []);

  /**********/
  /* upload */
  /**********/
  const {enqueueSnackbar} = useSnackbar();
  const reduxDispatch = useAppDispatch();

  const upload = useChunkedUpload({
    url: `${apiBaseUrl}/map`,
    params: {name: formik.values.name, path: formik.values.path},
    onComplete: () => {
      const message = `Map uploaded and is being deployed...`;
      enqueueSnackbar(message, {
        variant: 'success',
        action: CloseSnackbarAction,
      });
      formik.setFieldValue('file', null);
      reduxDispatch(reduxActions.assets.fetchMaps);
      onSubmitted?.();
    },
    onError: (error) => {
      const messages = getMessagesFromApiError(error, 'STRA_7a8a1d52');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
      formik.setFieldValue('file', null);
    },
  });

  return (
    <Box
      component="form"
      display="flex"
      flexDirection="column"
      gap={4}
      position="relative"
      onSubmit={formik.handleSubmit}
    >
      <TextField
        value={formik.values.name ?? ''}
        fullWidth
        name="name"
        label="Name"
        size="small"
        disabled={upload.isSubmitting}
        error={!!formik.touched.name && !!formik.errors.name}
        helperText={formik.touched.name && formik.errors.name}
        onChange={(event) => {
          formik.setFieldValue('name', event.target.value || null);
          if (shouldGeneratePath) {
            formik.setFieldValue('path', kebabCase(event.target.value) || null);
          }
        }}
      />

      <TextField
        value={formik.values.path ?? ''}
        fullWidth
        name="path"
        label="Path"
        size="small"
        disabled
        error={!!formik.touched.path && !!formik.errors.path}
        helperText={formik.touched.path && formik.errors.path}
      />

      <Box position="relative" border="1px solid" borderColor="grey.600">
        <Box
          p={2}
          textAlign="center"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
        >
          <Box display="flex" alignItems="center" height="100px">
            <Box>
              <Box>
                <CloudUploadIcon fontSize="large" />
              </Box>

              <Box>
                {formik.values.file
                  ? formik.values.file.name
                  : 'Choose map to upload'}
              </Box>
            </Box>

            {!formik.values.file && (
              <TextField
                type="file"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                }}
                InputProps={{
                  inputProps: {sx: {height: '100%', cursor: 'pointer'}},
                  sx: {height: '100%'},
                }}
                onChange={(event: any) =>
                  formik.setFieldValue('file', event.target.files[0])
                }
              />
            )}
          </Box>

          {upload.isSubmitting || upload.progress >= 100 ? (
            <Box width="100%">
              <Box mb={1}>{upload.progress}%</Box>

              <Box mb={3}>
                <LinearProgress
                  variant="determinate"
                  value={upload.progress ?? 0}
                />
              </Box>

              <Box>
                {upload.progress < 100 ? (
                  <Button
                    color="warning"
                    variant="contained"
                    onClick={() => {
                      upload.cancel();
                      formik.setFieldValue('file', null);
                    }}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button
                    color="success"
                    variant="contained"
                    onClick={() => {
                      upload.cancel();
                      formik.setFieldValue('file', null);
                    }}
                  >
                    Finish
                  </Button>
                )}
              </Box>
            </Box>
          ) : (
            <Box>
              {!!formik.values.file && (
                <Box textAlign="center">
                  <Box>
                    <Button onClick={() => formik.setFieldValue('file', null)}>
                      Clear
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
        <Button onClick={() => onCancel?.()}>Cancel</Button>

        <LoadingButton
          sx={{ml: 1}}
          variant="contained"
          loading={upload.isSubmitting}
          type="submit"
        >
          Upload
        </LoadingButton>
      </Box>
    </Box>
  );
};
